import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import {
    Box,
    Paper,
    Tabs,
    Tab,
    Stack,
    Typography,
    Divider,
    Chip,
    Grid,
    Link,
    Card,
    CardContent,
    Button,
} from "@mui/material";
import produce from "immer";
import { useAppDispatch } from "../../app/hooks";
import { LoadingContainer } from "../../app/LoadingContainer";
import { ErrorContainer } from "../../app/ErrorContainer";
import { CounterpartyDetails, CounterpartyRoleTypeEnum, loadCounterpartyDetails } from "./counterpartyApi";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import LanguageIcon from "@mui/icons-material/Language";
import BusinessIcon from "@mui/icons-material/Business";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import ArchiveIcon from "@mui/icons-material/Archive";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import { IdentifierTypeEnum } from "../common/commonTypes";
import { CounterpartyHeader } from "./components/CounterpartyHeader";
import { CounterpartyAddressesTab } from "./components/CounterpartyAddressesTab";
import { CounterpartyPeopleTab } from "./components/CounterpartyPeopleTab";
import { CounterpartyChannelsTab } from "./components/CounterpartyChannelsTab";
import { CounterpartyNotesTab } from "./components/CounterpartyNotesTab";
import { Localized } from "@fluent/react";
import { AddressTypeBadge } from "../common/AddressTypeBadge";
import { IdentifierTypeChip } from "../common/IdentifierTypeChip";

export const CounterpartiesForm = () => {
    const { id } = useParams<{ id: string }>();
    const dispatch = useAppDispatch();

    const [data, setData] = useState<CounterpartyDetails | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<any>(null);

    const handleUpdated = useCallback((updated: CounterpartyDetails) => {
        setData((prev) =>
            prev
                ? produce(prev, (draft) => {
                    Object.assign(draft as any, updated);
                })
                : updated
        );
    }, []);

    const handleReload = useCallback(() => {
        if (!id) return;
        dispatch(loadCounterpartyDetails({ publicId: id }))
            .then(unwrapResult)
            .then((dto) => setData(dto))
            .catch((e) => console.error("Failed to reload counterparty:", e));
    }, [id, dispatch]);

    useEffect(() => {
        let mounted = true;

        if (!id) {
            setError(new Error("Missing id"));
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        dispatch(loadCounterpartyDetails({ publicId: id }))
            .then(unwrapResult)
            .then((dto) => {
                if (!mounted) return;
                setData(dto);
            })
            .catch((e) => {
                if (!mounted) return;
                setError(e);
            })
            .finally(() => mounted && setIsLoading(false));

        return () => {
            mounted = false;
        };
    }, [id, dispatch]);

    if (isLoading) return <LoadingContainer />;
    if (error) return <ErrorContainer error={error} />;
    if (!data) return null;

    return (
        <CounterpartiesFormInternal
            counterparty={data}
            onUpdated={handleUpdated}
            onReload={handleReload}
        />
    );
};

export const CounterpartiesFormInternal = ({
    counterparty,
    onUpdated,
    onReload,
}: {
    counterparty: CounterpartyDetails;
    onUpdated: (updated: CounterpartyDetails) => void;
    onReload: () => void;
}) => {
    const [rolesOpen, setRolesOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    const [tab, setTab] = useState(0);

    const handleEditAddress = (addressId: number) => {
        console.log("edit address", addressId);
        // TODO: otwórz dialog/route
    };

    const handleEditPerson = (personId: number) => {
        console.log("edit person", personId);
    };

    const handleEditChannel = (channelId: number) => {
        console.log("edit channel", channelId);
    };

    const primaryAddress =
        counterparty.addresses.find(a => a.isPrimary) ?? counterparty.addresses[0];

    const primaryPerson =
        counterparty.people.find(p => p.isPrimary) ?? counterparty.people[0];

    const primaryCompanyEmail =
        counterparty.channels.find(c => c.channelType === "EMAIL" && c.isPrimary)?.value
        ?? counterparty.channels.find(c => c.channelType === "EMAIL")?.value;

    const primaryCompanyPhone =
        counterparty.channels.find(c => (c.channelType === "PHONE" || c.channelType === "MOBILE") && c.isPrimary)?.value
        ?? counterparty.channels.find(c => (c.channelType === "PHONE" || c.channelType === "MOBILE"))?.value;

    const addressLine = primaryAddress
        ? [primaryAddress.street, primaryAddress.buildingNo].filter(Boolean).join(" ")
        : null;

    const cityLine = primaryAddress
        ? [primaryAddress.postalCode, primaryAddress.city].filter(Boolean).join(" ")
        : null;


    return (
        <Paper elevation={0} sx={{ p: 2 }}>

            <CounterpartyHeader
                counterparty={counterparty}
                onEdit={() => setEditOpen(true)}
            />
            <Grid container spacing={2} sx={{ mb: 2, mt: 1 }}>
                <Grid item xs={12} md={6} lg={3}>
                    <Paper sx={{ p: 2, height: "100%" }} variant="outlined">
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <BusinessOutlinedIcon />
                                <Typography variant="subtitle1">
                                    <Localized id="about">
                                        <span>_about</span>
                                    </Localized>
                                </Typography>
                            </Stack>
                            <Divider />

                            <Stack spacing={1}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        <Localized id="counterparties-legalName">
                                            <span>_Nazwa prawna</span>
                                        </Localized>
                                    </Typography>
                                    <Typography variant="body2">{counterparty.legalName ?? "—"}</Typography>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        <Localized id="counterparties-primary-identifier">
                                            <span>_identyfikator</span>
                                        </Localized>
                                    </Typography>
                                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                        {counterparty.identifiers
                                            .filter((i) => i.isPrimary)
                                            .slice(0, 3)
                                            .map((i) => (
                                                <Stack key={i.id} direction="row" spacing={1} alignItems="center">
                                                    <IdentifierTypeChip type={i.identifierType} />
                                                    <Typography variant="body2">
                                                        {i.value}
                                                        {(i.countryCode || i.registerCode) && (
                                                            <Typography component="span" variant="caption" color="text.secondary">
                                                                {" "}
                                                                ({[i.countryCode, i.registerCode].filter(Boolean).join(" / ")})
                                                            </Typography>
                                                        )}
                                                    </Typography>
                                                </Stack>
                                            ))}

                                        {counterparty.identifiers.filter((i) => i.isPrimary).length === 0 && (
                                            <Typography variant="body2">—</Typography>
                                        )}
                                    </Stack>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        <Localized id="description">
                                            <span>_description</span>
                                        </Localized>
                                    </Typography>
                                    <Typography variant="body2">{counterparty.notes ?? "—"}</Typography>

                                </Box>

                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                    <Paper sx={{ p: 2, height: "100%" }} variant="outlined">
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PersonIcon />
                                <Typography variant="subtitle1">
                                    <Localized id="counterparties-account-manager">
                                        <span>Opiekun</span>
                                    </Localized>
                                </Typography>
                            </Stack>
                            <Divider />

                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    <Localized id="counterparties-account-manager">
                                        <span>Opiekun</span>
                                    </Localized>
                                </Typography>
                                <Typography variant="body2">{counterparty.accountManagerFullName ?? "—"}</Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Grid>

                {/* Primary address */}
                <Grid item xs={12} md={6} lg={3}>
                    <Paper sx={{ p: 2, height: "100%" }} variant="outlined">
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PlaceOutlinedIcon />
                                <Typography variant="subtitle1">
                                    <Localized id="counterparties-primary-address">
                                        <span>_Adres główny</span>
                                    </Localized>
                                </Typography>
                            </Stack>
                            <Divider />

                            <Stack spacing={1}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        <Localized id="counterparties-country">
                                            <span>_Kraj</span>
                                        </Localized>
                                    </Typography>
                                    <Typography variant="body2">
                                        {primaryAddress?.countryCode ?? counterparty.countryCode ?? "—"}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        <Localized id="counterparties-address">
                                            <span>_Adres</span>
                                        </Localized>
                                    </Typography>
                                    <Typography variant="body2">{addressLine ?? primaryAddress?.line1 ?? "—"}</Typography>
                                    <Typography variant="body2">{cityLine ?? primaryAddress?.line2 ?? ""}</Typography>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        <Localized id="counterparties-address-type">
                                            <span>_Typ adresu</span>
                                        </Localized>
                                    </Typography>
                                    <AddressTypeBadge type={primaryAddress?.addressType} />
                                </Box>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>

                {/* Primary contact */}
                <Grid item xs={12} md={6} lg={3}>
                    <Paper sx={{ p: 2, height: "100%" }} variant="outlined">
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PersonOutlineOutlinedIcon />
                                <Typography variant="subtitle1">
                                    <Localized id="counterparties-primary-contact">
                                        <span>_Kontakt główny</span>
                                    </Localized>
                                </Typography>
                            </Stack>
                            <Divider />

                            <Stack spacing={1}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        <Localized id="counterparties-person">
                                            <span>_Osoba</span>
                                        </Localized>
                                    </Typography>
                                    <Typography variant="body2">
                                        {primaryPerson ? `${primaryPerson.firstName} ${primaryPerson.lastName}` : "—"}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        <Localized id="counterparties-position">
                                            <span>_position</span>
                                        </Localized>
                                    </Typography>
                                    <Typography variant="body2">{primaryPerson?.jobTitle ?? "—"}</Typography>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        <Localized id="counterparties-department">
                                            <span>_Dział</span>
                                        </Localized>
                                    </Typography>
                                    <Typography variant="body2">{primaryPerson?.department ?? "—"}</Typography>
                                </Box>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>

                {/* Primary company channels */}
                <Grid item xs={12} md={6} lg={3}>
                    <Paper sx={{ p: 2, height: "100%" }} variant="outlined">
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <AlternateEmailOutlinedIcon />
                                <Typography variant="subtitle1">
                                    <Localized id="counterparties-company-channels">
                                        <span>_Kanały kontaktu (firma)</span>
                                    </Localized>
                                </Typography>
                            </Stack>
                            <Divider />

                            <Stack spacing={1}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        <Localized id="counterparties-email">
                                            <span>_Email</span>
                                        </Localized>
                                    </Typography>
                                    <Typography variant="body2">{primaryCompanyEmail ?? "—"}</Typography>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        <Localized id="counterparties-phone">
                                            <span>_Telefon</span>
                                        </Localized>
                                    </Typography>
                                    <Typography variant="body2">{primaryCompanyPhone ?? "—"}</Typography>
                                </Box>

                                <Box>
                                    <Stack spacing={0.3}>
                                        <Typography variant="caption" color="text.secondary">
                                            <Localized id="counterparties-website">
                                                <span>WWW</span>
                                            </Localized>
                                        </Typography>

                                        {counterparty.website ? (
                                            <Typography
                                                variant="body2"
                                                component="a"
                                                href={
                                                    counterparty.website.startsWith("http")
                                                        ? counterparty.website
                                                        : `https://${counterparty.website}`
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    color: "primary.main",
                                                    textDecoration: "none",
                                                    "&:hover": { textDecoration: "underline" },
                                                }}
                                            >
                                                {counterparty.website}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2">—</Typography>
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{ mb: 2 }}
                >
                    <Tab label={<Localized id="addresses"><span>_Adresy</span></Localized>} />
                    <Tab label={<Localized id="contacts"><span>_Kontakty</span></Localized>} />
                    <Tab label={<Localized id="channels"><span>_Kanały</span></Localized>} />
                    <Tab label={<Localized id="notes"><span>_Notatki</span></Localized>} />
                </Tabs>

                {tab === 0 && (
                    <CounterpartyAddressesTab
                        addresses={counterparty.addresses}
                        onEdit={(id) => console.log("edit", id)}
                        onAdd={() => console.log("add new address")}
                        onDelete={() => console.log("delete address")}
                        onSetPrimary={() => console.log("set primary address")}
                    />
                )}
                {tab === 1 && (
                    <CounterpartyPeopleTab
                        people={counterparty.people}
                        onEdit={handleEditPerson}
                    />
                )}
                {tab === 2 && (
                    <CounterpartyChannelsTab
                        channels={counterparty.channels}
                        onEdit={handleEditChannel}
                    />
                )}
                {tab === 3 &&
                    <CounterpartyNotesTab notes={(counterparty as any).notes} />
                }
            </Paper>
        </Paper>
    );
};
